import { useMutation } from '@apollo/client';
import { Button, ButtonSwitch, TextField } from '@elipssolution/harfang';
import { alpha, Checkbox, FormControlLabel, Stack, styled } from '@mui/material';
import { SyntheticEvent, useMemo } from 'react';
import { Control, Controller } from 'react-hook-form';

import { useConfirmationDialogContext } from '../../../../../components/ConfirmationDialogProvider';
import { generateErrorInformations } from '../../../../../utils/errorHandler';
import { REMOVE_PROCEDURE, RemoveProcedureType } from '../../../api/procedure';
import { ProcedureFormType, ProcedureType } from '../../../types/procedure';
import { VisibilityLabelsTypeEnum, VisibilityTypeEnum } from '../../../types/visibility';

const ErrorWrapper = styled('div')(({ theme: { palette, spacing, shape } }) => ({
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
	alignItems: 'center',
	gap: spacing(3),

	padding: spacing(0, 3),

	borderRadius: shape.borderRadius * 1.5,

	backgroundColor: alpha(palette.error.main, 0.3),
}));

const FormWrapper = styled(Stack)(({ theme: { spacing } }) => ({
	alignItems: 'flex-start',
	gap: spacing(2),
}));

const ButtonSwitchWrapper = styled('div')({
	minWidth: 200,
});

const buttonSwitchItems = Object.values(VisibilityTypeEnum).map((id) => ({ id, label: VisibilityLabelsTypeEnum[id] }));

type GlobalFormProps = {
	control: Control<ProcedureFormType>;
	procedureId?: ProcedureType['id'];
	onRemoveProcedure?: () => void;
};

const GlobalForm = ({ control, procedureId, onRemoveProcedure }: GlobalFormProps) => {
	const { confirmAction } = useConfirmationDialogContext();

	const [removeProcedure, { error: removeProcedureMutationError, reset: resetRemoveProcedureMutationData }] =
		useMutation<RemoveProcedureType>(REMOVE_PROCEDURE);

	const errorMessage = useMemo(
		() =>
			removeProcedureMutationError &&
			generateErrorInformations({ error: removeProcedureMutationError, resource: 'sign_removeProcedure' }).message,
		[removeProcedureMutationError],
	);

	const handleRemoveButtonClick = async () => {
		const isRemovalConfirmed = await confirmAction({
			title: 'Êtes-vous sûr de vouloir supprimer la procédure ?',
			confirmButtonDetails: {
				color: 'error',
				label: 'Supprimer',
			},
		});

		if (!isRemovalConfirmed) return null;

		resetRemoveProcedureMutationData();

		const result = await removeProcedure({
			variables: {
				signRemoveProcedureId: procedureId,
			},
		});

		onRemoveProcedure?.();

		return result;
	};

	return (
		<FormWrapper>
			<Stack alignItems="center" direction="row" gap={2} width="100%">
				<ButtonSwitchWrapper>
					<Controller
						name="global.isPrivate"
						control={control}
						defaultValue={VisibilityTypeEnum.PRIVATE === buttonSwitchItems[0].id}
						render={({ field: { value, onChange } }) => (
							<ButtonSwitch
								selectedItem={value ? VisibilityTypeEnum.PRIVATE : VisibilityTypeEnum.PUBLIC}
								items={buttonSwitchItems}
								onClick={(id) => onChange(id === VisibilityTypeEnum.PRIVATE)}
							/>
						)}
					/>
				</ButtonSwitchWrapper>

				<Controller
					rules={{ required: true }}
					name="global.name"
					control={control}
					render={({ field, fieldState: { error } }) => (
						<TextField {...field} invalid={!!error} label="Nom de la procédure" required />
					)}
				/>
			</Stack>

			<Controller
				name="global.comment"
				control={control}
				render={({ field }) => <TextField {...field} rows={4} multiline label="Commentaire" />}
			/>

			<Controller
				name="global.isAutomaticRemindersEnabled"
				defaultValue={false}
				control={control}
				render={({ field: { onChange, value, ...field } }) => {
					const handleChange = (_: SyntheticEvent<Element, Event>, newValue: boolean) => onChange(newValue);

					return (
						<FormControlLabel
							control={<Checkbox {...field} color="secondary" checked={value} onChange={handleChange} />}
							label="Activer la relance automatique des signataires (Toutes les semaines pendant cinq semaines)"
							sx={{ mt: -1 }}
						/>
					);
				}}
			/>

			{procedureId && (
				<Stack direction="row" alignItems="stretch" gap={2}>
					<Button color="error" variant="outlined" onClick={handleRemoveButtonClick}>
						Supprimer la procédure
					</Button>
					{errorMessage && <ErrorWrapper>{errorMessage}</ErrorWrapper>}
				</Stack>
			)}
		</FormWrapper>
	);
};

export default GlobalForm;
